import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssignmentTemplateDeleteApiOptions {
  templateId: string | number
  onStart?: () => void
  onSuccess?: () => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `DELETE /admin-api/assignment-templates/{template_id}` — a hard delete. There is no 409 in-use
 *  guard yet (no instance table); add the `blocked` branch when one lands. */
export const useAssignmentTemplateDeleteApi = async (
  options: AssignmentTemplateDeleteApiOptions,
): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assignment-templates.destroy', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return false
    }

    const { data } = await $axios.delete<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return false
    }

    toast.success('Assignment template deleted')
    options.onSuccess?.()
    return true
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return false
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
